"use client";

import { useCallback, useEffect, useRef } from "react";

import {
  clearCheckoutIntent,
  consumeCheckoutIntent,
  hasCheckoutSuccessParam,
  type CheckoutIntent,
  type CheckoutSurface,
} from "@/lib/billing/checkout-intent-stash";
import { useResetOnBfcache } from "@/lib/use-reset-on-bfcache";

/**
 * Reopens the credits & plans modal when the user comes back from Stripe
 * Checkout without paying — browser back, the Stripe "← back" chevron, or a
 * cancel (all of which land on the bare return URL with no success flag).
 *
 * `onResume` is called with what the user had selected; the host reopens its
 * RatedCreditsModal on that screen with that plan/amount seeded.
 *
 * Two ways back, both covered:
 *  - Full page load: the host mounts and the effect below runs.
 *  - Back-forward cache: the page is restored with React state intact and NO
 *    remount, so no effect would fire — hence the `pageshow` hook. (The flow
 *    already had to reckon with this: see useResetOnBfcache in
 *    rated-credits-flow, which unpins the `busy` CTA on the same restore.)
 *
 * A completed purchase returns with ?upgraded / ?topup and gets the success
 * confirmation instead; we only drop the stash on that path, never reopen over
 * the confirmation the host is showing.
 */
export function useCheckoutIntentResume(
  surface: CheckoutSurface,
  onResume: (intent: CheckoutIntent) => void,
) {
  // Kept in a ref so a host passing an inline arrow doesn't re-run the effect on
  // every render — and, more importantly, can't consume the stash twice.
  const onResumeRef = useRef(onResume);
  useEffect(() => {
    onResumeRef.current = onResume;
  }, [onResume]);

  const attempt = useCallback(() => {
    if (typeof window === "undefined") return;
    // Paid and back: the host's reconcile-on-return effect owns this arrival and
    // is putting up the purchase confirmation. Reopening the paywall over a
    // just-completed purchase would be the worst possible moment for it.
    if (hasCheckoutSuccessParam(window.location.search)) {
      clearCheckoutIntent();
      return;
    }
    const intent = consumeCheckoutIntent(window.location.pathname, surface);
    if (intent) onResumeRef.current(intent);
  }, [surface]);

  useEffect(() => {
    attempt();
  }, [attempt]);

  useResetOnBfcache(attempt);
}
